import { createSelector } from '@reduxjs/toolkit';
import { IMessageProps } from 'Components/Core';
import { ChangeEventWithData } from 'Components/Core/types';
import { SetValuePayLoad } from 'Components/Worksheets/Core/WorksheetEditorInjectedPropsProvider/types';
import { RootState } from 'Data/Redux';
import { toIQuantity } from 'Data/ViewModels/Core/IQuantity';
import { Message } from 'Data/ViewModels/IApiDataModel';
import { hasValue } from 'Lib/Any';
import * as oPath from 'Lib/Object/Path/Immutable';
import { Selector } from 'react-redux';
import {
  StandardisationInjectedConsumerProps,
  StandardisationInjectedPropsModifier,
  StandardisationInjectedProviderData,
  StandardisationInjectedProviderProps,
} from '../../types';

const getValue = (value: any) => {
  if (value && value.uom) return value.value; // Extract value from unit model
  if (value === undefined) return null; // Remove undefined

  return value;
};

const getMessagesForPath = (standardisation: any, path: string) => {
  let pathArray = path.split('.');

  const propName = pathArray.pop();

  pathArray.push('Messages');
  const messagesData = oPath.get(standardisation, pathArray.join('.'));

  const messages =
    messagesData &&
    messagesData
      .filter((m: Message) => m.PropertyName === propName)
      .map((m: Message) => ({ message: m.Text, severity: m.MessageType?.toLowerCase() }));

  if (messages) return messages;
  else return [];
};

const getConfigValue = (config: any, name: string) => {
  if (!config || !name) return null;
  if (typeof name !== 'string') return null;

  // Try to find a config for the exact name.
  if (config[name]) return config[name];

  // If there isn't one, remove array indices from the top level down to look for a generalised config.
  var arrayIndexRegex = /\[[0-9]*\]/;
  while (arrayIndexRegex.test(name)) {
    name = name.replace(arrayIndexRegex, '');
    if (config[name]) return config[name];
  }

  return null;
};

const standardisationSelector = (state: RootState) => state.gaugeStandardisation?.syncModel;

const createValueSelector = (name: string) =>
  createSelector(standardisationSelector, (standardisation) => ({ value: getValue(oPath.get(standardisation, name)) }));

const createConfigSelector = (name: string) =>
  createSelector(
    standardisationSelector,
    (state: RootState) => state.gaugeStandardisation?.config,
    (standardisation, config) => {
      let configProps = (name && getConfigValue(config, name)) || {};
      if (typeof configProps === 'function') {
        configProps = configProps({ name, equipment: standardisation });
      }
      if (typeof configProps.format === 'function') {
        configProps = {
          ...configProps,
          format: configProps.format.bind(null, { name, equipment: standardisation }),
        };
      }
      return configProps;
    }
  );

const createUomSelector = (configSelector: Selector<RootState, any>, valueSelector: Selector<RootState, any>) =>
  createSelector(configSelector, valueSelector, (config, value) => config?.uom || value?.uom); //consumerUom || value?.uom);

const createLabelSelector = (
  configSelector: Selector<RootState, any>,
  uomSelector: Selector<RootState, any>,
  consumerLabel: string | undefined
) =>
  createSelector(configSelector, uomSelector, (config, uom) => {
    let label = consumerLabel || config?.label;
    label = label && uom ? `${label} (${uom})` : label;
    return { label };
  });

const createFormatSelector = (configSelector: Selector<RootState, any>, consumerFormat: any | undefined) =>
  createSelector(configSelector, (config) => ({
    format: consumerFormat || config?.format,
  }));

const createMessagesSelector = (name: string, consumerMessages: IMessageProps[] | undefined = []) =>
  createSelector(standardisationSelector, (standardisation) => ({
    messages: getMessagesForPath(standardisation, name).concat(consumerMessages),
  }));

const createOnChangeSelector = (
  uomSelector: Selector<RootState, string | undefined>,
  //consumerOnChange: ((_: ChangeEventWithData) => void) | undefined,
  name: string,
  setValue: (update: SetValuePayLoad) => void,
  synchronise: () => void
) =>
  createSelector(uomSelector, (uom) => ({
    onChange:
      // consumerOnChange
      //   ? // Consumer onChange used without modification
      //     consumerOnChange
      //   : // Default onChange - setValue and synchronise depending on data package on event
      (evt: ChangeEventWithData) => {
        let value = evt.data?.value;

        if (uom && hasValue(value)) {
          // Quantity fields have a UOM, so the value they emit should be a bit different to other fields. Doing it here because
          // individual inputs shouldn't really need to know about quantities, they just emit a value. It's also convenient to
          // do here because we have the config property 'uom'.
          value = toIQuantity(value, uom);
        }

        if (name !== undefined && value !== undefined) {
          setValue({ name, value });
        }
        if (evt.data?.submitValues) synchronise();
      },
  }));

/**
 * Creates the createStandardisationSelector property for a consumer given its prefixName function.
 */
const createCreateStandardisationSelector =
  (standardisationSelector: Selector<RootState, any>, prefixName: any) =>
  /**
   * Selects a value from the worksheet for the given path
   * @param {string} path
   */
  (path: string = '') =>
    createSelector(standardisationSelector, (worksheet) => {
      return oPath.get(worksheet, prefixName(path));
    });

// Only props you want to override on the consumer need to be returned.
export const getStandardisationProviderProps = (
  consumerProps: StandardisationInjectedConsumerProps,
  providerData: StandardisationInjectedProviderData,
  modifiers: Array<StandardisationInjectedPropsModifier>
) => {
  let mutableConsumerProps = { ...consumerProps };

  // Apply modifiers to consumer props
  modifiers.forEach((m) => m(mutableConsumerProps, providerData));

  let {
    label: consumerLabel,
    name,
    namePrefix,
    prefixName: consumerPrefixName,
    messages: consumerMessages,
    format: consumerFormat,
    selectors: consumerSelectors,
  } = mutableConsumerProps;
  let { equipmentId, setValue, synchronise } = providerData;

  // Create a default prefixName that doesn't modify the name
  let prefixName = consumerPrefixName || ((n: any) => n);

  name = name && prefixName(name);

  let providerProps: StandardisationInjectedProviderProps = {
    namePrefix,
    prefixName,
    equipmentId,
    hasInjectedProps: true,
    createStandardisationSelector: createCreateStandardisationSelector(standardisationSelector, prefixName),
  };

  providerProps.selectors = consumerSelectors;
  if (name) {
    const valueSelector = createValueSelector(name);
    const configSelector = createConfigSelector(name);
    const uomSelector = createUomSelector(configSelector, valueSelector);

    providerProps.selectors = {
      ...providerProps.selectors,
      config: configSelector,
      label: createLabelSelector(configSelector, uomSelector, consumerLabel),
      messages: createMessagesSelector(name, consumerMessages),
      format: createFormatSelector(configSelector, consumerFormat),
      value: valueSelector,
      onChange: createOnChangeSelector(uomSelector, name, setValue, synchronise),
    };
  }

  return providerProps;
};
